import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AddToArtlistIcon from '@/assets/all-icons/add-to-artlist-icon.jsx';
import AlreadyFramedArtworkIcon from '@/assets/all-icons/already-framed-artwork-icon.jsx';
import CertificateOfAuthenticityIcon from '@/assets/all-icons/certificate-of-authenticity-icon.jsx';
import CircleAddedIcon from '@/assets/all-icons/circle-added-icon';
import LimitedEditionIcon from '@/assets/all-icons/limited-edition-icon.jsx';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import usePreferredLocale from '@/hooks/usePreferredLocale';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import AudioPlayer from '@/pages/artwork/content-section/components/AudioPlayer';
import { getLocalizedArtworkMedium } from '@/pages/artwork/utils/artworkMediumDisplay.utils';
import { formatArtworkTitleForDisplay } from '@/pages/artwork/utils/artworkTitleDisplay.utils';
import {
  getArtworkAiView,
  getArtworkArtistModalData,
  getArtworkHoldingType,
  getArtworkLimitedEditionHoldings,
  getArtworkLimitedEditionOptions,
  getArtworkTransactionSource,
  getArtworkViewerContext,
  isViewerDisplayedArtistOwner,
} from '@/pages/artwork/utils/artworkView.selectors';
import ArtworksOfArtist from '@/pages/profile/artists/artworks-of-artist';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import ArtworkSocials from '@/shared-components/artwork/components/ArtworkSocials';
import LimitedEditionSummary from '@/shared-components/artwork/limited-edition-summary';
import FollowButton from '@/shared-components/buttons/components/FollowButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import AutoMarquee from '@/shared-components/text/components/AutoMarquee';
import { isRestrictedArtwork, shouldHideCollectorInteractions } from '@/utils/artworkVisibility';
import { resolveLocalizedPublicPath } from '@/utils/localePublicUrl';

import useEditionStatus from '../hooks/useEditionStatus';
import useLECopy from '../hooks/useLECopy';
import { resolveLimitedEditionContext } from '../utils/limitedEdition.utils';

import {
  buildArtist,
  buildLimitedEditionHoldingOwners,
  buildLimitedEditionOwners,
  buildMetadataSoldHistoryFallback,
  buildOwnershipSectionData,
  formatSoldHistoryDate,
  formatSoldHistoryPrice,
  getSoldHistory,
  selectPreferredSoldHistoryEntries,
  splitSoldHistoryEntries,
} from './artworkMoreDetailsDesktop.helpers';
import AuctionTimeLeftIndicator from './AuctionTimeLeftIndicator';
import LEsOwnershipCard from './LEsOwnershipCard';

import './styles/ArtworkMoreDetailsDesktop.css';

function PrivateOwnerIndicator({ label }) {
  return (
    <div className="artwork-more-details__desktop__owner-private">
      <svg
        className="artwork-more-details__desktop__owner-lock-icon"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <rect x="3.5" y="8.5" width="13" height="9" rx="3.5" ry="3.5" />
        <path d="M6.5 8.5v-2.2a3.5 3.5 0 1 1 7 0v2.2" />
        <circle cx="10" cy="12.5" r="1.35" />
      </svg>
      <span className="artwork-more-details__desktop__owner-private-label">{label}</span>
    </div>
  );
}

function OwnershipRowPlaceholder() {
  return (
    <div
      className="artwork-more-details__desktop__profile-row artwork-more-details__desktop__profile-row--placeholder"
      aria-hidden="true"
    >
      <div className="artwork-more-details__desktop__profile-image artwork-more-details__desktop__profile-image--placeholder" />
      <div className="artwork-more-details__desktop__profile-texts">
        <span className="artwork-more-details__desktop__placeholder-line artwork-more-details__desktop__placeholder-line--name" />
        <span className="artwork-more-details__desktop__placeholder-line artwork-more-details__desktop__placeholder-line--location" />
      </div>
    </div>
  );
}

export default function ArtworkMoreDetailsDesktop({
  artworkData,
  socialsData,
  onOpenThoughts,
  onOpenRatings,
  onOpenAskAi,
  onLikeUpdate,
  hasLimitedEditionAvailable = false,
  onOpenLimitedEdition,
  onOpenAddToArtlist = null,
  isGalleryOwnerView = false,
}) {
  const t = useTranslations('common.shared.artwork_view');
  const artworkContext = useContext(ArtworkDataContext);
  const locale = usePreferredLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, cacheHandler } = useStateHandler();
  const userId = state?.user_session?._id ?? null;
  const { artwork_about: artworkAbout, artwork_media: artworkMedia } = artworkData;
  const viewerContext = getArtworkViewerContext(artworkData);
  const artworkId = artworkData?._id ?? null;
  const isAuctionArtwork = Boolean(artworkAbout?.artwork_is_auction);
  const auctionEndsAt = artworkData?.artwork_auction?.ends_at ?? null;
  const cachedArtworkPickerArtlists = userId
    ? cacheHandler.getCacheValue(`${userId}_artwork_view_artlists`)?.data ?? null
    : null;
  const limitedEditionContext = useMemo(
    () => resolveLimitedEditionContext(artworkData),
    [artworkData],
  );
  const productMetadata = limitedEditionContext.metadata;
  const editionNumber = limitedEditionContext.editionNumber;
  const editionSizeFromMetadata = limitedEditionContext.editionSize;
  const isLimitedEditionProduct = limitedEditionContext.isLimitedEdition;
  const productId = artworkData?.product_id ?? null;
  const holdingType = getArtworkHoldingType(artworkData);
  const isLimitedEditionHolding = holdingType === 'limited_edition';
  const viewerType = state?.user_session?.user_type ?? state?.user_session?.__t ?? null;
  const searchParams = new URLSearchParams(location.search);
  const holdingTypeOverride =
    searchParams.get('holding_type')
    ?? location.state?.artworkViewContext?.holdingType
    ?? null;
  const hideCollectorInteractions = shouldHideCollectorInteractions(
    artworkData,
    viewerType,
    holdingTypeOverride,
  );
  const canOpenAskAi = useMemo(() => {
    if (typeof onOpenAskAi !== 'function') {
      return false;
    }
    return getArtworkAiView(artworkData).canOpenAskAi;
  }, [artworkData?.artwork_ai, onOpenAskAi]);
  const limitedEditionOptions = useMemo(
    () => getArtworkLimitedEditionOptions(artworkData, { fallbackHoldingMetadata: isLimitedEditionHolding }),
    [artworkData, isLimitedEditionHolding],
  );
  const openLimitedEditionCollectors = artworkContext.openLimitedEditionCollectors;

  const {
    data: editionStatusData,
    loading: editionStatusLoading,
  } = useEditionStatus(productId, {
    enabled: isLimitedEditionProduct && Boolean(productId),
  });

  const {
    data: limitedEditionCopy,
    loading: limitedEditionCopyLoading,
  } = useLECopy(
    productId,
    editionNumber,
    {
      enabled: isLimitedEditionProduct
        && Boolean(productId)
        && (editionNumber || editionNumber === 0),
    },
  );

  // ---------- NUEVO: estado para abrir el modal al estilo HomeUserCard ----------
  const [openArtworksModal, setOpenArtworksModal] = useState(false);
  const [artistDataForModal, setArtistDataForModal] = useState(null); // objeto artist enriquecido c/ user_type forzado
  const [modalQueryData, setModalQueryData] = useState({ cacheKey: '', endpoint: '' });

  // plantillas (mismo sistema que HomeUserCard)
  const cacheKeyTpl = 'artworks_of_{0}_in_search';
  const endpointArtist = '/galleryartist/{0}/artworks';
  const endpointGalleryArtist = '/galleryartist/{0}/artworks';

  // ---------------------------------------------------------------------------

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [canExpandDescription, setCanExpandDescription] = useState(false);
  const descriptionRef = useRef(null);
  const [limitedEditionBlockedPopupOpen, setLimitedEditionBlockedPopupOpen] = useState(false);

  const renderCertificateOwnershipLink = (label) => (
    <a
      href={resolveLocalizedPublicPath('/certificate-of-ownership', locale)}
      target="_blank"
      rel="noopener noreferrer"
      className="artwork-more-details__desktop__extra-info-link"
    >
      {label}
    </a>
  );
  const artist = useMemo(
    () => buildArtist(artworkAbout, viewerContext),
    [artworkAbout, viewerContext?.artist, viewerContext?.gallery_artist],
  );
  const {
    headingKey: ownershipHeadingKey = 'gallery',
    profile: ownershipProfile,
    isPrivateOwner,
    showFollowButton: canFollowOwnership,
    isOwnerView,
    isOriginalProduct,
    fallbackName: ownershipFallbackName,
    isAwaitingOwnershipIdentity,
  } = useMemo(
    () => buildOwnershipSectionData(artworkData, state.user_session, viewerContext),
    [
      artworkAbout,
      artworkData?.artwork_status,
      artworkData?.artwork_flags,
      artworkData?.ownership_context,
      state.user_session,
      viewerContext?.gallery,
    ],
  );

  const limitedEditionDisabled = isGalleryOwnerView || isRestrictedArtwork(artworkData);
  const limitedEditionDisabledMessage = limitedEditionDisabled
    ? t('details.limited_edition_disabled_message')
    : null;
  const isHoldingView = Boolean(
    holdingType
    || artworkData?.metadata?.holding_id
    || artworkData?.product_metadata?.holding_id
    || artworkData?.holding_id,
  );
  const ownershipDisplayName = ownershipProfile?.name
    ?? ownershipProfile?.username
    ?? ownershipFallbackName
    ?? '—';
  const ownershipLocation = ownershipProfile?.location ?? '';
  const ownershipProfileImage = ownershipProfile?.image ?? null;
  const ownershipProfileType = ownershipProfile?.type
    ?? (ownershipHeadingKey === 'gallery' ? 'gallery' : 'collector');
  const ownershipUserId = ownershipProfile?.id ?? null;
  const ownershipUsername = ownershipProfile?.username ?? '';
  const ownershipImFollowing = ownershipProfile?.imFollowing ?? false;
  const ownershipIsFollowingMe = ownershipProfile?.isFollowingMe ?? false;
  const canNavigateToOwnershipProfile = Boolean(ownershipUsername);
  const privateOwnerDisplayLabel = t('details.private_owner');
  const ownershipHeadingDisplay = isLimitedEditionHolding
    ? (ownershipHeadingKey === 'gallery'
      ? t('details.ownership_heading.uploaded_by')
      : t('details.ownership_heading.original_artwork_owner'))
    : t(`details.headings.${ownershipHeadingKey}`);
  const shouldHoldOwnershipIdentity = Boolean(
    ownershipHeadingKey === 'owner'
    && isAwaitingOwnershipIdentity,
  );

  const transactionHistoryEntries = useMemo(() => {
    const src = getArtworkTransactionSource(artworkData);
    return getSoldHistory(src, { artworkData });
  }, [artworkData]);

  const metadataSoldHistoryEntries = useMemo(
    () => buildMetadataSoldHistoryFallback(artworkData),
    [artworkData],
  );

  const limitedEditionHoldingsRaw = useMemo(
    () => getArtworkLimitedEditionHoldings(artworkData),
    [artworkData],
  );

  const limitedEditionHoldingOwners = useMemo(
    () => buildLimitedEditionHoldingOwners(limitedEditionHoldingsRaw),
    [limitedEditionHoldingsRaw],
  );

  const { limitedEdition: limitedEditionSoldHistoryEntries } = useMemo(
    () => splitSoldHistoryEntries(transactionHistoryEntries),
    [transactionHistoryEntries],
  );

  const {
    limitedEdition: metadataLimitedEditionSoldHistoryEntries,
  } = useMemo(
    () => splitSoldHistoryEntries(metadataSoldHistoryEntries),
    [metadataSoldHistoryEntries],
  );
  const buildOwnerKey = useCallback((owner) => {
    if (!owner) {
      return '';
    }
    const identity = owner.username ?? owner.name ?? owner.id ?? '';
    const editionKey = owner.editionNumber ?? owner.editionSize ?? '';
    return `${identity}-${editionKey}`;
  }, []);

  const limitedEditionOwners = useMemo(() => {
    let historyOwners = [];
    if (limitedEditionSoldHistoryEntries.length > 0) {
      historyOwners = buildLimitedEditionOwners(limitedEditionSoldHistoryEntries);
    } else if (metadataLimitedEditionSoldHistoryEntries.length > 0) {
      historyOwners = buildLimitedEditionOwners(metadataLimitedEditionSoldHistoryEntries);
    }

    if (!limitedEditionHoldingOwners.length) {
      return historyOwners;
    }

    if (!historyOwners.length) {
      return limitedEditionHoldingOwners;
    }

    const dedupeSet = new Set(
      historyOwners.map((owner) => owner.id ?? buildOwnerKey(owner)),
    );

    const merged = [...historyOwners];
    limitedEditionHoldingOwners.forEach((owner) => {
      const dedupeKey = owner.id ?? buildOwnerKey(owner);
      if (!dedupeSet.has(dedupeKey)) {
        merged.push(owner);
        dedupeSet.add(dedupeKey);
      }
    });

    return merged;
  }, [
    limitedEditionSoldHistoryEntries,
    metadataLimitedEditionSoldHistoryEntries,
    limitedEditionHoldingOwners,
  ]);

  const limitedEditionOwnersFiltered = useMemo(
    () => limitedEditionOwners.filter(
      (owner) =>
        owner?.isLimitedEdition === true
        || Number.isFinite(owner?.editionNumber)
        || Number.isFinite(owner?.editionSize),
    ),
    [limitedEditionOwners],
  );

  const isOriginalPublicOwnerProfile = useMemo(
    () => (isOriginalProduct && ownershipHeadingKey === 'owner' && isPrivateOwner !== true),
    [isOriginalProduct, isPrivateOwner, ownershipHeadingKey],
  );
  const soldVia = typeof artworkData?.artwork_flags?.sold_via === 'string'
    ? artworkData.artwork_flags.sold_via.trim().toLowerCase()
    : '';
  const unclaimedOwner = Boolean(
    productMetadata?.unclaimed_owner === true
    || productMetadata?.unclaimed_email
  );
  const pendingClaim = Boolean(
    artworkData?.artwork_flags?.claim_code
    || artworkData?.artwork_flags?.claim_email
    || artworkData?.artwork_flags?.sold_contact?.email
    || artworkData?.artwork_flags?.sold_contact?.id
    || soldVia === 'in_store_suarte'
    || unclaimedOwner,
  );
  const canUseMetadataSoldHistory = isOwnerView || isOriginalPublicOwnerProfile || pendingClaim;

  const soldHistoryEntries = useMemo(
    () => selectPreferredSoldHistoryEntries({
      isLimitedEditionProduct,
      isOriginalProduct,
      canUseMetadata: canUseMetadataSoldHistory,
      allowLimitedEditionMetadata: isOwnerView,
      transactionHistoryEntries,
      metadataSoldHistoryEntries,
    }),
    [
      canUseMetadataSoldHistory,
      isLimitedEditionProduct,
      isOriginalProduct,
      isOwnerView,
      metadataSoldHistoryEntries,
      transactionHistoryEntries,
    ],
  );

  const hasSoldHistoryEntries = soldHistoryEntries.length > 0;
  const shouldShowSoldHistory = !isLimitedEditionProduct && hasSoldHistoryEntries;
  const soldHistoryEntriesToDisplay = soldHistoryEntries;
  const {
    artwork_medium: medium,
    artwork_size: size,
    artwork_year: year,
    artwork_includes_certificate: includesCertificate,
    artwork_includes_frame: includesFrame,
    artwork_title: title,
    artwork_description: description,
  } = artworkAbout;

  const trimmedDescription = typeof description === 'string' ? description.trim() : '';

  const formattedSize = size ? `${size.length} x ${size.height} ${size.unit}` : '-';

  const isArtistOwner = isViewerDisplayedArtistOwner(artworkData, state.user_session);
  const canFollowArtist = Boolean(artist.id) && !isArtistOwner;

  // ---------- NUEVO: construimos el dato del artista para el modal, forzando user_type ----------
  const artistModalData = useMemo(() => getArtworkArtistModalData(artworkData), [artworkData]);

  // ---------- NUEVO: abrir modal con el mismo sistema que HomeUserCard ----------
  const openArtistArtworks = () => {
    if (!artistModalData || !artistModalData._id) return;

    const forcedType = artistModalData.user_type === 'gallery_artist' ? 'gallery_artist' : 'artist';
    const cacheKey = cacheKeyTpl.replace('{0}', artistModalData._id);
    const endpoint =
      forcedType === 'gallery_artist'
        ? endpointGalleryArtist.replace('{0}', artistModalData._id)
        : endpointArtist.replace('{0}', artistModalData._id);

    setArtistDataForModal(artistModalData);
    setModalQueryData({ cacheKey, endpoint });
    setOpenArtworksModal(true);
  };

  const handleArtistClick = () => {
    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.ARTIST_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'artwork-more-details__desktop__profile-row' },
      });
    }
    if (artist?.username) {
      navigate(`/user/${artist.username}`);
      return;
    }
    openArtistArtworks();
  };

  const handleArtistKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleArtistClick();
    }
  };

  const handleOwnershipProfileClick = () => {
    if (!canNavigateToOwnershipProfile) {
      return;
    }

    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.GALLERY_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'artwork-more-details__desktop__profile-row' },
      });
    }

    navigate(`/user/${ownershipUsername}`);
  };

  const handleOwnershipProfileKeyDown = (event) => {
    if (!canNavigateToOwnershipProfile) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOwnershipProfileClick();
    }
  };

  // ---------------------------------------------------------------------------------------------

  useEffect(() => {
    const node = descriptionRef.current;
    if (!node) {
      setCanExpandDescription(false);
      return;
    }
    if (!trimmedDescription) {
      setIsDescriptionExpanded(false);
      setCanExpandDescription(false);
      return;
    }
    setIsDescriptionExpanded(false);
    const frame = requestAnimationFrame(() => {
      if (!descriptionRef.current) return;
      const hasOverflow = descriptionRef.current.scrollHeight - descriptionRef.current.clientHeight > 2;
      setCanExpandDescription(hasOverflow);
    });
    return () => cancelAnimationFrame(frame);
  }, [trimmedDescription]);

  const collapseDescription = () => {
    setIsDescriptionExpanded(false);

    requestAnimationFrame(() => {
      const node = descriptionRef.current;
      if (!node) return;

      // An expanded description may have been scrolled. Reset it before its
      // clipped layout returns so the inline "more" control remains visible.
      node.scrollTop = 0;
      setCanExpandDescription(node.scrollHeight - node.clientHeight > 2);
    });
  };

  const detailTitle = formatArtworkTitleForDisplay(title) || '-';
  const mediumText = getLocalizedArtworkMedium(medium, locale);
  const detailMetadata = [mediumText, formattedSize, year || '-'].join(',\u00A0\u00A0');
  const displayedDescription = trimmedDescription || '-';
  const artworkAudio = artworkMedia.artwork_audio?.audio_id;
  const viewerDisplayName = state?.user_session?.user_name
    ?? state?.user_session?.user_username
    ?? null;
  const viewerId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const viewerUsername = state?.user_session?.user_username ?? null;

  const limitedEditionCardLoading = editionStatusLoading || limitedEditionCopyLoading;
  const limitedEditionEditionSize = useMemo(
    () => (
      limitedEditionCopy?.editionSize
      ?? editionStatusData?.editionSize
      ?? editionSizeFromMetadata
      ?? null
    ),
    [
      editionSizeFromMetadata,
      editionStatusData?.editionSize,
      limitedEditionCopy?.editionSize,
    ],
  );

  const limitedEditionSaleFallback = useMemo(() => {
    if (!isLimitedEditionProduct) {
      return null;
    }

    const normalizedEditionNumber = Number(editionNumber);
    const targetEditionNumber = Number.isFinite(normalizedEditionNumber)
      ? normalizedEditionNumber
      : null;

    const historyCandidates = limitedEditionSoldHistoryEntries.length > 0
      ? limitedEditionSoldHistoryEntries
      : (isOwnerView ? metadataLimitedEditionSoldHistoryEntries : []);

    const pickMatch = (entries) => {
      if (!Array.isArray(entries) || entries.length === 0) {
        return null;
      }
      if (targetEditionNumber !== null) {
        const matched = entries.find(
          (entry) => Number(entry?.editionNumber) === targetEditionNumber,
        );
        if (matched) {
          return matched;
        }
      }
      return entries[0] ?? null;
    };

    const historyMatch = pickMatch(historyCandidates);
    if (historyMatch) {
      return {
        price:
          historyMatch.price
          ?? historyMatch.priceBreakdown?.net
          ?? historyMatch.priceBreakdown?.gross
          ?? null,
        currency: historyMatch.currency ?? artworkData?.artwork_about?.artwork_currency ?? null,
        soldAt: historyMatch.date ?? historyMatch.dateRaw ?? null,
      };
    }

    const holdingMatch = (() => {
      if (!limitedEditionHoldingOwners.length) {
        return null;
      }
      if (targetEditionNumber !== null) {
        const exact = limitedEditionHoldingOwners.find(
          (owner) => Number(owner?.editionNumber) === targetEditionNumber,
        );
        if (exact) {
          return exact;
        }
      }
      return limitedEditionHoldingOwners[0] ?? null;
    })();

    if (holdingMatch) {
      return {
        price: holdingMatch.price ?? null,
        currency: holdingMatch.currency ?? artworkData?.artwork_about?.artwork_currency ?? null,
        soldAt: holdingMatch.date ?? null,
      };
    }

    return null;
  }, [
    artworkData?.artwork_about?.artwork_currency,
    editionNumber,
    isLimitedEditionProduct,
    isOwnerView,
    limitedEditionHoldingOwners,
    limitedEditionSoldHistoryEntries,
    metadataLimitedEditionSoldHistoryEntries,
  ]);

  const limitedEditionFallbackPrice = useMemo(() => ({
    amount: limitedEditionSaleFallback?.price ?? editionStatusData?.lastPurchasePrice ?? null,
    currency: limitedEditionSaleFallback?.currency ?? editionStatusData?.currency ?? null,
  }), [
    editionStatusData?.currency,
    editionStatusData?.lastPurchasePrice,
    limitedEditionSaleFallback?.currency,
    limitedEditionSaleFallback?.price,
  ]);

  const limitedEditionSoldAtFallback = limitedEditionSaleFallback?.soldAt ?? null;

  const limitedEditionIsPrivate = (limitedEditionCopy?.isPrivate ?? productMetadata?.is_private) === true;
  const limitedEditionOwnerFallbackName = useMemo(() => {
    const candidate =
      limitedEditionHoldingOwners[0]
      ?? limitedEditionOwnersFiltered[0]
      ?? null;
    return candidate?.name ?? candidate?.username ?? ownershipDisplayName ?? '—';
  }, [limitedEditionHoldingOwners, limitedEditionOwnersFiltered, ownershipDisplayName]);
  const limitedEditionOwnerFallbackImage = useMemo(() => {
    const candidate =
      limitedEditionHoldingOwners[0]
      ?? limitedEditionOwnersFiltered[0]
      ?? null;
    return candidate?.imageId ?? candidate?.image_id ?? candidate?.image ?? null;
  }, [limitedEditionHoldingOwners, limitedEditionOwnersFiltered]);
  const limitedEditionOwnerFallbackType = useMemo(() => {
    const candidate =
      limitedEditionHoldingOwners[0]
      ?? limitedEditionOwnersFiltered[0]
      ?? null;
    return candidate?.type ?? candidate?.profile?.type ?? candidate?.profile?.user_type ?? null;
  }, [limitedEditionHoldingOwners, limitedEditionOwnersFiltered]);

  const limitedEditionOwnerIsViewer = useMemo(() => {
    const ownerCandidate =
      limitedEditionCopy?.currentOwner
      ?? limitedEditionCopy?.owner
      ?? limitedEditionHoldingOwners[0]?.profile
      ?? limitedEditionHoldingOwners[0]
      ?? null;

    if (!ownerCandidate) {
      return false;
    }

    if (typeof ownerCandidate === 'string') {
      return Boolean(
        (viewerId && ownerCandidate === viewerId)
        || (viewerUsername && ownerCandidate === viewerUsername),
      );
    }

    const ownerId = ownerCandidate._id ?? ownerCandidate.id ?? null;
    const ownerUsername = ownerCandidate.username ?? ownerCandidate.user_username ?? null;

    return Boolean(
      (viewerId && ownerId && ownerId === viewerId)
      || (viewerUsername && ownerUsername && ownerUsername === viewerUsername),
    );
  }, [limitedEditionCopy, limitedEditionHoldingOwners, viewerId, viewerUsername]);
  const shouldRenderLimitedEditionCard = useMemo(() => {
    if (!isLimitedEditionProduct) {
      return false;
    }

    if (limitedEditionIsPrivate && !isOwnerView) {
      return false;
    }

    if (!productId) {
      return true;
    }

    const shouldRender = Boolean(
      limitedEditionCopy
      || editionStatusData
      || limitedEditionCardLoading
      || editionNumber
      || limitedEditionEditionSize
    );
    return shouldRender;
  }, [
    editionNumber,
    editionStatusData,
    isLimitedEditionProduct,
    limitedEditionCardLoading,
    limitedEditionCopy,
    limitedEditionEditionSize,
    limitedEditionIsPrivate,
    isOwnerView,
    productId,
  ]);

  const limitedEditionEntries = useMemo(
    () => Object.entries(limitedEditionOptions || {}).filter(([, entry]) => entry),
    [limitedEditionOptions],
  );
  const fallbackHasLimitedEditionAvailable = useMemo(() => {
    return limitedEditionEntries.some(([, edition]) => {
      const { quantity, sold } = edition;
      if (typeof quantity === 'number' && typeof sold === 'number') {
        return quantity > sold;
      }
      return true;
    });
  }, [limitedEditionEntries]);

  const shouldRenderLimitedEditionSummary =
    limitedEditionEntries.length > 0
    || hasLimitedEditionAvailable
    || fallbackHasLimitedEditionAvailable
    || isLimitedEditionHolding;
  const isAdminOnly = useMemo(() => isRestrictedArtwork(artworkData), [artworkData]);
  const limitedEditionBadgeOverride = useMemo(() => {
    if (!isAdminOnly || !isLimitedEditionHolding || !limitedEditionHoldingOwners.length) {
      return null;
    }

    const normalizeSizeKey = (value) =>
      (typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '_') : null);

    const formatSizeLabel = (value) => {
      if (typeof value !== 'string') {
        return null;
      }
      const cleaned = value.trim().replace(/[_-]+/g, ' ');
      if (!cleaned) {
        return null;
      }
      return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const holdingId =
      artworkData?.metadata?.holding_id
      ?? artworkData?.product_metadata?.holding_id
      ?? artworkData?.holding_id
      ?? null;

    const matchOwner =
      limitedEditionHoldingOwners.find((owner) => holdingId && owner?.id === holdingId)
      ?? limitedEditionHoldingOwners.find(
        (owner) => Number.isFinite(Number(editionNumber))
          && Number(owner?.editionNumber) === Number(editionNumber),
      )
      ?? limitedEditionHoldingOwners[0];

    const sizeKey =
      normalizeSizeKey(matchOwner?.sizeKey)
      ?? normalizeSizeKey(matchOwner?.sizeLabel)
      ?? normalizeSizeKey(matchOwner?.size_label)
      ?? normalizeSizeKey(matchOwner?.size)
      ?? null;

    const ownersInGroup = sizeKey
      ? limitedEditionHoldingOwners.filter((owner) => {
        const ownerKey =
          normalizeSizeKey(owner?.sizeKey)
          ?? normalizeSizeKey(owner?.sizeLabel)
          ?? normalizeSizeKey(owner?.size_label)
          ?? normalizeSizeKey(owner?.size)
          ?? null;
        return ownerKey === sizeKey;
      })
      : limitedEditionHoldingOwners;

    if (!ownersInGroup.length) {
      return null;
    }

    const indexInGroup = ownersInGroup.findIndex((owner) => owner?.id === matchOwner?.id);
    const sequenceNumber = indexInGroup >= 0 ? indexInGroup + 1 : 1;
    const sequenceTotal = ownersInGroup.length;
    const sizeLabel = formatSizeLabel(matchOwner?.sizeLabel ?? matchOwner?.sizeKey ?? matchOwner?.size);
    if (sizeLabel) {
      return `${sizeLabel} ${sequenceNumber}/${sequenceTotal}`;
    }
    return `${sequenceNumber}/${sequenceTotal}`;
  }, [
    artworkData?.holding_id,
    artworkData?.metadata?.holding_id,
    artworkData?.product_metadata?.holding_id,
    editionNumber,
    isAdminOnly,
    isLimitedEditionHolding,
    limitedEditionHoldingOwners,
  ]);
  const isArtworkInAnyArtlist = useMemo(() => {
    const artlists = cachedArtworkPickerArtlists?.data;
    if (!artworkId || !Array.isArray(artlists) || artlists.length === 0) {
      return false;
    }
    return artlists.some((artlist) => {
      if (typeof artlist?.artlist_contains_artwork === 'boolean') {
        return artlist.artlist_contains_artwork;
      }
      const artlistArtworks = artlist?.artlist_artworks ?? [];
      return artlistArtworks.some((artwork) => artwork?._id === artworkId);
    });
  }, [
    cachedArtworkPickerArtlists?.data,
    artworkId,
    state.triggeredActions.ADD_TO_ARTLIST_FROM_ARTWORK,
  ]);
  return (
    <div className="artwork-more-details__desktop__sections">
      <div className="artwork-more-details__desktop__overview">
        <section className="artwork-more-details__desktop__section artwork-more-details__desktop__section--details">
          {isAuctionArtwork && auctionEndsAt ? (
            <div className="artwork-more-details__desktop__auction-timer">
              <AuctionTimeLeftIndicator endsAt={auctionEndsAt} />
            </div>
          ) : null}
          <div className="artwork-more-details__desktop__primary-info">
            <div className="artwork-more-details__desktop__title-row">
              <AutoMarquee
                className="artwork-more-details__desktop__primary-marquee"
                contentClassName="artwork-more-details__desktop__title-value"
                dependencies={[detailTitle]}
              >
                {detailTitle}
              </AutoMarquee>
              {isLimitedEditionHolding ? (
                <LimitedEditionIcon className="artwork-more-details__desktop__title-le-icon" />
              ) : null}
              {onOpenAddToArtlist && !hideCollectorInteractions ? (
                <button
                  type="button"
                  className="artwork-more-details__desktop__add-to-artlist-button"
                  aria-label={t('mobile.artwork_label.add_to_artlist_aria')}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenAddToArtlist?.();
                  }}
                >
                  {isArtworkInAnyArtlist ? (
                    <CircleAddedIcon className="artwork-more-details__desktop__add-to-artlist-icon artwork-more-details__desktop__add-to-artlist-icon--added" />
                  ) : (
                    <AddToArtlistIcon className="artwork-more-details__desktop__add-to-artlist-icon" />
                  )}
                </button>
              ) : null}
            </div>
            <AutoMarquee
              className="artwork-more-details__desktop__primary-marquee artwork-more-details__desktop__primary-marquee--meta"
              contentClassName="artwork-more-details__desktop__meta-value"
              dependencies={[detailMetadata]}
            >
              {detailMetadata}
            </AutoMarquee>
          </div>

        </section>
        <section className="artwork-more-details__desktop__section artwork-more-details__desktop__section--description">
          {artworkAudio ? <AudioPlayer audio={artworkAudio} /> : null}
          <div className="artwork-more-details__desktop__description-content">
            <div
              ref={descriptionRef}
              className={`artwork-more-details__desktop__description-text ${isDescriptionExpanded ? 'expanded' : ''}`}
            >
              {displayedDescription}
              {canExpandDescription && !isDescriptionExpanded && (
                <button
                  type="button"
                  className="element-non-selectable artwork-view-artwork-description__more-button artwork-view-artwork-description__more-button--inline"
                  onClick={() => setIsDescriptionExpanded(true)}
                >
                  {t('description.more')}
                </button>
              )}
            </div>

            {canExpandDescription && isDescriptionExpanded && (
              <button
                type="button"
                className="element-non-selectable artwork-view-artwork-description__more-button"
                onClick={collapseDescription}
              >
                {t('description.less')}
              </button>
            )}
          </div>

          {!hideCollectorInteractions && (
            <div className="artwork-desktop-view__socials">
              <ArtworkSocials
                artworkData={artworkData}
                socialsData={socialsData}
                onClickThought={() => onOpenThoughts?.()}
                onClickRating={() => onOpenRatings?.()}
                onClickAskAi={canOpenAskAi ? () => onOpenAskAi?.() : null}
                onClickLike={(status) => onLikeUpdate?.(status)}
              />
            </div>
          )}
        </section>
      </div>
      <hr className="artwork-more-details__desktop__divider" />
      {shouldRenderLimitedEditionSummary && (
        <section className="artwork-more-details__desktop__section artwork-more-details__desktop__section--limited-edition">
          <LimitedEditionSummary
            editions={limitedEditionOptions}
            currency={artworkData?.artwork_about?.artwork_currency}
            onEditionSelect={(sizeKey, sizeData) => onOpenLimitedEdition?.({ sizeKey, sizeData })}
            disabled={limitedEditionDisabled}
            onDisabledSelect={() => setLimitedEditionBlockedPopupOpen(true)}
            hideList={isAdminOnly}
            onShowOwners={openLimitedEditionCollectors}
          />
          <hr className="artwork-more-details__desktop__divider" />
        </section>
      )}
      <section className="artwork-more-details__desktop__section artwork-more-details__desktop__section--artist">
        <h3 className="artwork-more-details__desktop__heading">{t('details.headings.artist')}</h3>
        <div className="artwork-more-details__desktop__profile-row">
          <UserProfileImage
            image={artist.image}
            typeOfProfile={artist.type}
            className="artwork-more-details__desktop__profile-image"
            onClick={handleArtistClick}
          />
          <div className="artwork-more-details__desktop__profile-texts">
            <span
              className="artwork-more-details__desktop__profile-name"
              role="button"
              tabIndex={0}
              onClick={handleArtistClick}
              onKeyDown={handleArtistKeyDown}
            >
              {artist.name}
            </span>
            <span className="artwork-more-details__desktop__profile-location">{artist.location}</span>
          </div>
          {canFollowArtist && (
            <FollowButton
              className="artwork-view__follow-button artwork-more-details__desktop__follow-button font-size-small"
              userId={artist.id}
              followeeType={artist?.type === 'gallery_artist' ? 'gallery_artist' : 'user'}
              imFollowing={artist.imFollowing}
              isFollowingMe={artist.isFollowingMe}
              userName={artist.name}
              userUsername={artist.username}
              showFollowingIcon />
          )}
        </div>
      </section>
      <hr className="artwork-more-details__desktop__divider" />
      <section className="artwork-more-details__desktop__section artwork-more-details__desktop__section--gallery">
        <h3 className="artwork-more-details__desktop__heading">{ownershipHeadingDisplay}</h3>
        {shouldHoldOwnershipIdentity ? (
          <OwnershipRowPlaceholder />
        ) : isPrivateOwner ? (
          <PrivateOwnerIndicator label={privateOwnerDisplayLabel} />
        ) : (
          <div className="artwork-more-details__desktop__profile-row">
            <UserProfileImage
              image={ownershipProfileImage}
              typeOfProfile={ownershipProfileType}
              className={`artwork-more-details__desktop__profile-image${
                canNavigateToOwnershipProfile ? ' artwork-more-details__desktop__profile-image--clickable' : ''
              }`}
              onClick={canNavigateToOwnershipProfile ? handleOwnershipProfileClick : undefined}
            />
            <div className="artwork-more-details__desktop__profile-texts">
              <span
                className={`artwork-more-details__desktop__profile-name${
                  canNavigateToOwnershipProfile ? ' artwork-more-details__desktop__profile-name--link' : ''
                }`}
                role={canNavigateToOwnershipProfile ? 'button' : undefined}
                tabIndex={canNavigateToOwnershipProfile ? 0 : undefined}
                onClick={canNavigateToOwnershipProfile ? handleOwnershipProfileClick : undefined}
                onKeyDown={canNavigateToOwnershipProfile ? handleOwnershipProfileKeyDown : undefined}
              >
                {ownershipDisplayName}
              </span>
              {ownershipLocation && (
                <span className="artwork-more-details__desktop__profile-location">
                  {ownershipLocation}
                </span>
              )}
            </div>
            {canFollowOwnership && ownershipUserId && (
              <FollowButton
                className="artwork-view__follow-button artwork-more-details__desktop__follow-button font-size-small"
                userId={ownershipUserId}
                imFollowing={ownershipImFollowing}
                isFollowingMe={ownershipIsFollowingMe}
                userName={ownershipDisplayName}
                userUsername={ownershipUsername}
                showFollowingIcon />
            )}
          </div>
        )}
      </section>
      {shouldShowSoldHistory && (
        <div className="artwork-more-details__desktop__sold-history">
          {/* <div className="artwork-more-details__desktop__sold-history-timeline">
            <div className="artwork-more-details__desktop__sold-history-line" />
            <div
              className="artwork-more-details__desktop__sold-history-dots"
              style={{ '--sold-history-dot-count': Math.max(1, soldHistoryEntriesToDisplay.length) }}
            >
              {soldHistoryEntriesToDisplay.map((entry) => (
                <span
                  key={entry.id}
                  className="artwork-more-details__desktop__sold-history-dot"
                />
              ))}
            </div>
          </div> */}
          <div className="artwork-more-details__desktop__sold-history-card">
            <span className="artwork-more-details__desktop__sold-history-title">{t('details.headings.sold_history')}</span>
            <div className="artwork-more-details__desktop__sold-history-table">
              <div className="artwork-more-details__desktop__sold-history-row artwork-more-details__desktop__sold-history-row--head">
                <span>{t('details.sold_history.columns.date')}</span>
                <span>{t('details.sold_history.columns.from')}</span>
                <span>{t('details.sold_history.columns.price')}</span>
              </div>
              {soldHistoryEntriesToDisplay.map((entry) => (
                <div key={entry.id} className="artwork-more-details__desktop__sold-history-row">
                  <span>{formatSoldHistoryDate(entry.date, entry.dateRaw)}</span>
                  <div className="artwork-more-details__desktop__sold-history-seller">
                    {entry.sellerProfile ? (
                      entry.sellerProfile.username ? (
                        <button
                          type="button"
                          className="artwork-more-details__desktop__sold-history-user"
                          onClick={() => navigate(`/user/${entry.sellerProfile.username}`)}
                        >
                          <UserProfileImage
                            image={entry.sellerProfile.image_id}
                            typeOfProfile={entry.sellerProfile.type ?? 'collector'}
                            className="artwork-more-details__desktop__sold-history-avatar"
                          />
                          <span className="artwork-more-details__desktop__sold-history-user-name">
                            {entry.sellerProfile.name ?? entry.seller ?? '—'}
                          </span>
                        </button>
                      ) : (
                        <span className="artwork-more-details__desktop__sold-history-user artwork-more-details__desktop__sold-history-user--static">
                          <UserProfileImage
                            image={entry.sellerProfile.image_id}
                            typeOfProfile={entry.sellerProfile.type ?? 'collector'}
                            className="artwork-more-details__desktop__sold-history-avatar"
                          />
                          <span className="artwork-more-details__desktop__sold-history-user-name">
                            {entry.sellerProfile.name ?? entry.seller ?? '—'}
                          </span>
                        </span>
                      )
                    ) : (
                      <span className="artwork-more-details__desktop__sold-history-user artwork-more-details__desktop__sold-history-user--static">
                        <span className="artwork-more-details__desktop__sold-history-user-name">
                          {entry.seller ?? '—'}
                        </span>
                      </span>
                    )}
                  </div>
                  <span>{formatSoldHistoryPrice(entry.price, entry.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {!isHoldingView && (
        <div className="artwork-more-details__desktop__extra-info">
          <h4 className="artwork-more-details__desktop__extra-info-heading">
            {t('details.extra_info.includes_heading')}
          </h4>
          {includesCertificate && (
            <div className="artwork-more-details__desktop__extra-info-row">
              <CertificateOfAuthenticityIcon className="artwork-more-details__desktop__extra-info-icon" />
              <span className="artwork-more-details__desktop__extra-info-text">
                {t('details.certificate.certificate_of_authenticity')}
                {` ${t('details.extra_info.issued_by_gallery_or_artist')}`}
              </span>
            </div>
          )}
          <div className="artwork-more-details__desktop__extra-info-row">
            <CertificateOfAuthenticityIcon className="artwork-more-details__desktop__extra-info-icon" />
            <span className="artwork-more-details__desktop__extra-info-text artwork-more-details__desktop__extra-info-text--link">
              {renderCertificateOwnershipLink(t('details.certificate.certificate_of_ownership'))}
              {` ${t('details.extra_info.provided_by_suarte')}`}
            </span>
          </div>
          {includesFrame && (
            <div className="artwork-more-details__desktop__extra-info-row">
              <AlreadyFramedArtworkIcon className="artwork-more-details__desktop__extra-info-icon" />
              <span className="artwork-more-details__desktop__extra-info-text">
                {t('details.extra_info.frame')}
              </span>
            </div>
          )}
        </div>
      )}
      {shouldRenderLimitedEditionCard ? (
        <div className="artwork-more-details__desktop__limited-owner-card">
          <LEsOwnershipCard
            copy={limitedEditionCopy}
            fallbackEditionNumber={editionNumber}
            fallbackEditionSize={limitedEditionEditionSize}
            fallbackOwnerName={limitedEditionOwnerFallbackName}
            fallbackOwnerImage={limitedEditionOwnerFallbackImage}
            fallbackOwnerType={limitedEditionOwnerFallbackType}
            fallbackPrice={limitedEditionFallbackPrice}
            badgeLabelOverride={limitedEditionBadgeOverride}
            loading={limitedEditionCardLoading}
            isOwnerView={limitedEditionOwnerIsViewer}
            viewerDisplayName={viewerDisplayName}
            fallbackSoldAt={limitedEditionSoldAtFallback}
          />
        </div>
      ) : null}
      {/* NUEVO: modal al estilo HomeUserCard con la misma info */}
      {openArtworksModal && artistDataForModal && (
        <ArtworksOfArtist
          open={openArtworksModal}
          close={() => setOpenArtworksModal(false)}
          artist={artistDataForModal}
          internal={false}
          showHeader={false}
          cacheKey={modalQueryData.cacheKey}
          queryEndpoint={modalQueryData.endpoint}
        />
      )}
      <ActionRejectedPopup
        open={limitedEditionBlockedPopupOpen}
        close={() => setLimitedEditionBlockedPopupOpen(false)}
        description={limitedEditionDisabledMessage}
      />
    </div>
  );
}
